// src/writer/components/CharactersView.tsx
import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { primaryButtonClass, dangerButtonClass } from '../constants';
import { 
    useViewEditMode, 
    ViewEditSection, 
    SaveCancelButtons, 
    DisplayField 
} from '../utils/viewEditUtils';
import ConfirmationModal from './ConfirmationModal';
import { useOptimizedSave } from '../hooks/useOptimizedSave';

// Character Creation Modal Component
const CharacterCreationModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (characterData: { name: string; role: string; briefDescription: string }) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        briefDescription: ''
    });

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            alert('Character name is required');
            return;
        }
        onSubmit(formData);
        setFormData({ name: '', role: '', briefDescription: '' });
        onClose();
    };

    const handleClose = () => {
        setFormData({ name: '', role: '', briefDescription: '' });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold mb-4">Add New Character</h3>
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Character Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter character name"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Role
                            </label>
                            <input
                                type="text"
                                value={formData.role}
                                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Protagonist, Antagonist, Supporting"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Brief Description
                            </label>
                            <textarea
                                value={formData.briefDescription}
                                onChange={(e) => setFormData(prev => ({ ...prev, briefDescription: e.target.value }))}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                                placeholder="Brief description of the character"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                        <button
                            type="submit"
                            className={`${primaryButtonClass} text-sm px-4 py-2 flex-1`}
                        >
                            Add Character
                        </button>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded text-sm flex-1"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CharactersView: React.FC = () => {
    const appContext = useAppContext();
    
    if (!appContext) {
        return (
            <div className="max-w-4xl mx-auto p-6 text-center">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading...</h2>
                    <p className="text-gray-700">
                        Please wait while the context loads.
                    </p>
                </div>
            </div>
        );
    }

    const { projectState, handleBlueprintChange } = appContext;
    const hasInitialBlueprint = projectState?.hasInitialBlueprint || false;
    
    // Get characters from blueprint structure with enhanced safety checks
    const worldBuildingData = projectState?.blueprint?.worldBuildingSuggestions ||
                           (projectState?.blueprint as any)?.worldBuildingData || { // Backward compatibility
        characters: [],
        locations: [],
        themes: [],
        conflicts: [],
        worldRules: [],
        culturalElements: [],
        technologyMagic: [],
        timeline: []
    };
    
    // Simplified character handling functions (placeholder implementations)
    const handleAddCharacter = () => {
    };
    
    const handleDeleteCharacter = (index: number) => {
        const updatedCharacters = worldBuildingData.characters.filter((_: any, i: number) => i !== index);
        handleBlueprintChange('worldBuildingSuggestions', 'characters', updatedCharacters);
    };
    
    const handleDeleteAllCharacters = () => {
        handleBlueprintChange('worldBuildingSuggestions', 'characters', []);
    };
    
    // Placeholder loading states
    const isRefineCharactersLoading = false;
    const isExpandCharactersLoading = false;
    
    // Character modification handlers (simplified)
    const handleCharacterChange = (index: number, field: string, value: string) => {
        const updatedCharacters = [...worldBuildingData.characters];
        if (updatedCharacters[index]) {
            updatedCharacters[index] = { ...updatedCharacters[index], [field]: value };
            handleBlueprintChange('worldBuildingSuggestions', 'characters', updatedCharacters);
        }
    };
    
    const handleCharacterNameChange = (index: number, value: string) => handleCharacterChange(index, 'name', value);
    const handleCharacterDescriptionChange = (index: number, value: string) => handleCharacterChange(index, 'briefDescription', value);
    const handleCharacterRoleChange = (index: number, value: string) => handleCharacterChange(index, 'role', value);
    const handleCharacterBackstoryChange = (index: number, value: string) => handleCharacterChange(index, 'backstory', value);
    const handleCharacterGoalsChange = (index: number, value: string) => handleCharacterChange(index, 'goals', value);
    const handleCharacterConflictsChange = (index: number, value: string) => handleCharacterChange(index, 'conflicts', value);
    const handleCharacterPersonalityChange = (index: number, value: string) => handleCharacterChange(index, 'personality', value);
    const handleCharacterAppearanceChange = (index: number, value: string) => handleCharacterChange(index, 'appearance', value);
    const handleCharacterRelationshipsChange = (index: number, value: string) => handleCharacterChange(index, 'relationships', value);
    const handleCharacterDevelopmentChange = (index: number, value: string) => handleCharacterChange(index, 'development', value);
    const handleCharacterNotesChange = (index: number, value: string) => handleCharacterChange(index, 'notes', value);
    
    // Placeholder for refine/expand functions
    const handleRefineCharacters = () => {
    };
    
    const handleExpandCharacters = () => {
    };

    // Enhanced debug logging to track data flow issues
    console.log('👥 CharactersView - Enhanced data flow debug:', {
        projectId: projectState?.projectId,
        hasInitialBlueprint,
        blueprintKeys: projectState?.blueprint ? Object.keys(projectState.blueprint) : 'no blueprint',
        worldBuildingSuggestions: projectState?.blueprint?.worldBuildingSuggestions,
        worldBuildingData: (projectState?.blueprint as any)?.worldBuildingData,
        finalWorldBuildingData: worldBuildingData,
        charactersCount: worldBuildingData?.characters?.length || 0,
        firstCharacter: worldBuildingData?.characters?.[0] || 'none',
        dataSource: worldBuildingData?.characters?.length > 0 ? 'blueprint data' : 'fallback empty array'
    });

    const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<{
        isOpen: boolean;
        type: 'single' | 'all';
        index?: number;
        name?: string;
    }>({ isOpen: false, type: 'single' });
    const { activeEditSection, handleEditToggle, handleSaveAndExit, handleCancelEdit } = useViewEditMode();
    const { saveWorldBuilding } = useOptimizedSave();

    const handleSaveChanges = async () => {
        try {
            // Get blueprint state management functions from optimized data context
            const { blueprint } = appContext.optimizedData || {};
            
            await saveWorldBuilding.mutateAsync({
                projectId: appContext.dbProject?.uuid || appContext.dbProject?.id?.toString() || '',
                worldBuildingData,
                setBlueprintSaving: blueprint?.setBlueprintSaving
            });
            handleSaveAndExit();
        } catch (error) {
            console.error('Failed to save changes:', error);
        }
    };

    const handleCustomAddCharacter = (characterData: { name: string; role: string; briefDescription: string }) => {
        // First add empty character
        handleAddCharacter();
        
        // Then update it with the form data
        const lastIndex = worldBuildingData.characters.length;
        setTimeout(() => {
            handleCharacterNameChange(lastIndex, characterData.name);
            handleCharacterChange(lastIndex, 'briefDescription', characterData.briefDescription);
            handleCharacterRoleChange(lastIndex, characterData.role);
        }, 0);
    };

    const handleConfirmDeleteCharacter = (index: number, name: string) => {
        setConfirmDelete({
            isOpen: true,
            type: 'single',
            index,
            name
        });
    };

    const handleConfirmDeleteAllCharacters = () => {
        setConfirmDelete({
            isOpen: true,
            type: 'all'
        });
    };

    const executeDelete = () => {
        if (confirmDelete.type === 'single' && confirmDelete.index !== undefined) {
            handleDeleteCharacter(confirmDelete.index);
        } else if (confirmDelete.type === 'all') {
            handleDeleteAllCharacters();
        }
        setConfirmDelete({ isOpen: false, type: 'single' });
    };

    const cancelDelete = () => {
        setConfirmDelete({ isOpen: false, type: 'single' });
    };

    if (!projectState) {
        return (
            <div className="max-w-4xl mx-auto p-6 text-center">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading...</h2>
                    <p className="text-gray-700">
                        Please wait while the project loads.
                    </p>
                </div>
            </div>
        );
    }

    if (!hasInitialBlueprint) {
        return (
            <div className="max-w-4xl mx-auto p-6 text-center">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-yellow-800 mb-2">Blueprint Required</h2>
                    <p className="text-yellow-700">
                        Please generate your initial blueprint from the "Idea Hub" tab before managing characters.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6" style={{backgroundImage: "url('/assets/texture_tile.png')", backgroundRepeat: 'repeat', backgroundSize: 'auto', minHeight: '100vh'}}>
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Characters</h1>
                <p className="text-gray-600">
                    Manage your story's characters. Characters are automatically extracted from your blueprint
                    and can be refined or expanded using AI assistance.
                </p>
            </div>

            <ViewEditSection
                title="Characters Management"
                subtitle="Add, edit, and refine your story characters"
                isActive={activeEditSection === 'characters'}
                onEditToggle={() => handleEditToggle('characters')}
                className="bg-white"
            >
                <div className="space-y-4">
                    {/* Action Buttons - Only show in edit mode */}
                    {activeEditSection === 'characters' && (
                        <div className="flex flex-wrap gap-2 mb-4">
                            <button
                                onClick={() => setIsCharacterModalOpen(true)}
                                className={`${primaryButtonClass} text-sm px-4 py-2`}
                            >
                                Add Character
                            </button>
                            {worldBuildingData.characters.length > 0 && (
                                <>
                                    <button
                                        onClick={handleRefineCharacters}
                                        disabled={isRefineCharactersLoading}
                                        className={`${primaryButtonClass} text-sm px-4 py-2 bg-green-600 hover:bg-green-700 focus:ring-green-500`}
                                    >
                                        {isRefineCharactersLoading ? 'Refining...' : 'AI Refine Characters'}
                                    </button>
                                    <button
                                        onClick={handleExpandCharacters}
                                        disabled={isExpandCharactersLoading}
                                        className={`${primaryButtonClass} text-sm px-4 py-2 bg-purple-600 hover:bg-purple-700 focus:ring-purple-500`}
                                    >
                                        {isExpandCharactersLoading ? 'Expanding...' : 'AI Expand Characters'}
                                    </button>
                                    <button
                                        onClick={handleConfirmDeleteAllCharacters}
                                        className={`${dangerButtonClass} text-sm px-4 py-2`}
                                    >
                                        Delete All Characters
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {/* Characters List */}
                    {worldBuildingData.characters.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 mb-4">No characters yet.</p>
                            <p className="text-sm text-gray-400">
                                Characters from your blueprint will appear here automatically, or you can add them manually.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {worldBuildingData.characters.map((char: any, index: number) => (
                                <div key={char.id} className="border rounded-lg bg-gray-50 p-4">
                                    {/* VIEW MODE: Show character info as display fields */}
                                    {activeEditSection !== 'characters' && (
                                        <>
                                            <div className="mb-4">
                                                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                                    {char.name || `Character ${index + 1}`}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <DisplayField
                                                        label="Role"
                                                        value={char.role}
                                                    />
                                                    <DisplayField
                                                        label="Brief Description" 
                                                        value={char.briefDescription}
                                                    />
                                                </div>
                                                {(char.description || char.backstory || char.goals || char.conflicts || char.personality || char.appearance || char.relationships || char.developmentArc || char.notes) && (
                                                    <details className="mt-4">
                                                        <summary className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
                                                            Show More Details
                                                        </summary>
                                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            {char.description && (
                                                                <DisplayField
                                                                    label="Detailed Description"
                                                                    value={char.description}
                                                                />
                                                            )}
                                                            {char.backstory && (
                                                                <DisplayField
                                                                    label="Backstory"
                                                                    value={char.backstory}
                                                                />
                                                            )}
                                                            {char.goals && (
                                                                <DisplayField
                                                                    label="Goals"
                                                                    value={char.goals}
                                                                />
                                                            )}
                                                            {char.conflicts && (
                                                                <DisplayField
                                                                    label="Conflicts"
                                                                    value={char.conflicts}
                                                                />
                                                            )}
                                                            {char.personality && (
                                                                <DisplayField
                                                                    label="Personality"
                                                                    value={char.personality}
                                                                />
                                                            )}
                                                            {char.appearance && (
                                                                <DisplayField
                                                                    label="Appearance"
                                                                    value={char.appearance}
                                                                />
                                                            )}
                                                            {char.relationships && (
                                                                <DisplayField
                                                                    label="Relationships"
                                                                    value={char.relationships}
                                                                />
                                                            )}
                                                            {char.developmentArc && (
                                                                <DisplayField
                                                                    label="Development Arc"
                                                                    value={char.developmentArc}
                                                                />
                                                            )}
                                                            {char.notes && (
                                                                <DisplayField
                                                                    label="Notes"
                                                                    value={char.notes}
                                                                />
                                                            )}
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {/* EDIT MODE: Show full editing interface */}
                                    {activeEditSection === 'characters' && (
                                        <>
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="text-lg font-semibold text-gray-800">
                                                    {char.name || `Character ${index + 1}`}
                                                </h4>
                                                <button
                                                    onClick={() => handleConfirmDeleteCharacter(index, char.name || `Character ${index + 1}`)}
                                                    className={`${dangerButtonClass} text-xs px-2 py-1`}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                            
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                        <input
                                                            type="text"
                                                            value={char.name || ''}
                                                            onChange={(e) => handleCharacterNameChange(index, e.target.value)}
                                                            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="Character name"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                                        <input
                                                            type="text"
                                                            value={char.role || ''}
                                                            onChange={(e) => handleCharacterRoleChange(index, e.target.value)}
                                                            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            placeholder="e.g., Protagonist, Antagonist, Supporting"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Brief Description</label>
                                                    <textarea
                                                        value={char.briefDescription || ''}
                                                        onChange={(e) => handleCharacterChange(index, 'briefDescription', e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Brief character description from blueprint"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Detailed Description</label>
                                                    <textarea
                                                        value={char.description || ''}
                                                        onChange={(e) => handleCharacterDescriptionChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={3}
                                                        placeholder="Detailed character description"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Backstory</label>
                                                    <textarea
                                                        value={char.backstory || ''}
                                                        onChange={(e) => handleCharacterBackstoryChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={3}
                                                        placeholder="Character backstory"
                                                    />
                                                </div>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Goals</label>
                                                        <textarea
                                                            value={char.goals || ''}
                                                            onChange={(e) => handleCharacterGoalsChange(index, e.target.value)}
                                                            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            rows={2}
                                                            placeholder="Character goals"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Conflicts</label>
                                                        <textarea
                                                            value={char.conflicts || ''}
                                                            onChange={(e) => handleCharacterConflictsChange(index, e.target.value)}
                                                            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            rows={2}
                                                            placeholder="Character conflicts"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Personality</label>
                                                    <textarea
                                                        value={char.personality || ''}
                                                        onChange={(e) => handleCharacterPersonalityChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Character personality traits"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Appearance</label>
                                                    <textarea
                                                        value={char.appearance || ''}
                                                        onChange={(e) => handleCharacterAppearanceChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Character physical appearance"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationships</label>
                                                    <textarea
                                                        value={char.relationships || ''}
                                                        onChange={(e) => handleCharacterRelationshipsChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Character relationships with others"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Development Arc</label>
                                                    <textarea
                                                        value={char.developmentArc || ''}
                                                        onChange={(e) => handleCharacterDevelopmentChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Character development throughout the story"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                                    <textarea
                                                        value={char.notes || ''}
                                                        onChange={(e) => handleCharacterNotesChange(index, e.target.value)}
                                                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        rows={2}
                                                        placeholder="Additional notes about the character"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {activeEditSection === 'characters' && (
                    <SaveCancelButtons
                        onSave={handleSaveChanges}
                        onCancel={handleCancelEdit}
                    />
                )}
            </ViewEditSection>

            {/* Character Creation Modal */}
            <CharacterCreationModal
                isOpen={isCharacterModalOpen}
                onClose={() => setIsCharacterModalOpen(false)}
                onSubmit={handleCustomAddCharacter}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmDelete.isOpen}
                title={confirmDelete.type === 'all' ? 'Delete All Characters?' : 'Delete Character?'}
                message={
                    confirmDelete.type === 'all' 
                        ? `Are you sure you want to delete all ${worldBuildingData.characters.length} characters? This action cannot be undone.`
                        : `Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`
                }
                onConfirm={executeDelete}
                onCancel={cancelDelete}
                type="danger"
            />
        </div>
    );
};

export default CharactersView;